import { Component, OnInit, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { Observable, of, forkJoin, Subject } from 'rxjs';
import { SortDescriptor, State } from '@progress/kendo-data-query';
import {
  GridDataResult,
  DataStateChangeEvent,
  PagerSettings,
  GridComponent,
  GridModule,
} from '@progress/kendo-angular-grid';
import { map, share, tap, takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CustomDialog } from '../../shared/components/modal-dialog/CustomDialog';
import { ModalResult } from '../../core/interfaces/modal-result';
import { ResultState } from '../../core/enums/result-state.enum';
import { ImpersonationService } from '../../core/services/impersonation.service';
import { GroupHeader } from '../../core/interfaces/group-header';
import { FilterHelper } from '../../shared/utils/FilterHelper';
import { PagingHelperService } from '../../core/services/paging-helper.service';
import { KeyValuePair } from '../../core/interfaces/key-value-pair';
import { CustomerSelectorOptions } from '../../core/models/CustomerSelectorOptions';
import { CustomerService } from '../../core/services/customer.service';
import { CustomerSearchFilter } from '../../core/interfaces/customer-search-filter';
import { CustomerHeader } from '../../core/interfaces/customer-header';
import { NotificationService } from '../../core/services/notification.service';
import { Router } from '@angular/router';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ModalDialogService } from '../../core/services/modal-dialog.service';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { PagerModule } from '@progress/kendo-angular-pager';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { AsyncPipe } from '@angular/common';
import { IconsModule } from '@progress/kendo-angular-icons';
import * as icons from '@progress/kendo-svg-icons';

interface KeyValuePairs<TKey, TValue> {
  key: TKey;
  value: TValue;
}

@Component({
  selector: 'show-all-customer',
  templateUrl: './show-all-customer.component.html',
  styleUrl: './show-all-customer.component.scss',
  providers: [TranslatePipe],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    GridModule,
    DropDownsModule,
    InputsModule,
    ButtonsModule,
    PagerModule,
    TooltipModule,
    TranslateModule,
    AsyncPipe,
    IconsModule,
  ],
})
export class ShowAllCustomerComponent extends CustomDialog implements OnInit, OnDestroy {
  public isPagingRequired = false;
  public excludePreliminaryFilter = false;
  public isLoading = false;
  public isSearchActive = false;
  public pagingConfig: PagerSettings | boolean = true;
  public hasAccess = true;
  icons = icons;

  public filterHelper = FilterHelper;
  public recentGroups: CustomerHeader[] = [];
  public filter: Array<KeyValuePair> = [];
  public customers: CustomerHeader[] = [];
  public searchOptions: KeyValuePairs<string, string>[] = [
    {
      key: 'customerName',
      value: 'GENERAL.GROUP_SELECTION.SHARED.CUSTOMER_NAME',
    },
    {
      key: 'contractNumber',
      value: 'GENERAL.GROUP_SELECTION.SHARED.CONTRACT_NUMBER',
    },
    { key: 'unitId', value: 'GENERAL.GROUP_SELECTION.SHARED.UNIT_ID' },
    {
      key: 'buildingAddress',
      value: 'GENERAL.GROUP_SELECTION.SHARED.BUILDING_ADDRESS',
    },
    { key: 'buildingCity', value: 'GENERAL.GROUP_SELECTION.SHARED.BUILDING_CITY' },
    {
      key: 'customerVat',
      value: 'GENERAL.GROUP_SELECTION.SHARED.CUSTOMER_VAT',
    },
  ];

  public state: State = {
    take: 10,
  };
  public accessibleGroups: Observable<GridDataResult>;
  public searchText: string;
  public tenantId: string;
  public unitSearchForm: FormGroup;
  public _unitFilter: CustomerSearchFilter;
  public selectedTenant: string;
  public options: CustomerSelectorOptions;
  public searchForm: FormGroup;

  readonly MaxButtonCount = 5;
  sortDirection = signal<SortDescriptor[]>([{ field: 'numberOfUnits', dir: 'desc' }]);

  @ViewChild(GridComponent) grid!: GridComponent;

  private _unsubscribe: Subject<void> = new Subject();
  private _numberOfAccessibleGroups: number;
  private _filterLookup: Map<string, string>;

  public dialog = inject(DialogRef);
  public modalDialogService = inject(ModalDialogService);
  private _router = inject(Router);
  private readonly _impersonationService = inject(ImpersonationService);
  private readonly _pagingHelperService = inject(PagingHelperService);
  private readonly _translatePipe = inject(TranslatePipe);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _customerService = inject(CustomerService);
  private _notificationService = inject(NotificationService);

  dialogParameters = this.modalDialogService.dialogParameters();
  tenants = this.dialogParameters.tenants;

  public ngOnInit(): void {
    this.hasAccess = this.dialogParameters?.isAccessToGroups ?? true;
    this.width = this.hasAccess ? 950 : 259;

    this.options =
      this.dialogParameters instanceof CustomerSelectorOptions
        ? this.dialogParameters
        : new CustomerSelectorOptions(true, false);

    if (!this.hasAccess) return;

    this._impersonationService.recentGroups
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(recentGroups => {
        this.recentGroups = recentGroups;
      });

    this.setFilterLookup();

    if (this.options.showTenantSelection) {
      this.selectedTenant = this._impersonationService.getCurrentTenantIdentifier();
    }

    this._customerService.customers$.subscribe(data => {
      this.customers = data;
      this.accessibleGroups = of({
        data: this.recentGroups,
        total: 10,
      });
    });

    this.unitSearchForm = this._formBuilder.group({
      filter: new FormControl(this.filter[0].value),
      tenantId: new FormControl(this.getCurrentTenantId(), [Validators.required]),
      option: new FormControl('customerName', [Validators.required]),
      term: new FormControl(''),
    });

    this._impersonationService.currentTenant
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(tenant => {
        this.tenantId = tenant;
      });
  }

  public ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  public closeSelector(): void {
    const result: ModalResult = { state: ResultState.Cancelled };

    this.close(result);
  }

  public dataStateChange(stateEvent: DataStateChangeEvent): void {
    this.state = stateEvent;
  }

  public preliminaryFilterChanged(event: any, filterService: any): void {
    this.excludePreliminaryFilter = event.target.checked;
    this.filterHelper.filterChange(event.target.checked, filterService, 'name');
  }

  public searchGroup(searchText: string): void {
    this.searchText = searchText;
    this.loadAccessibleGroups(true);
  }

  public loadAccessibleGroups(
    updateCount: boolean = false,
    sortDirection?: SortDescriptor[]
  ): void {
    if (sortDirection) {
      this.sortDirection.set(sortDirection);
    }
    if (updateCount) {
      this.state.skip = 0;
    }

    this.searchText = this.unitSearchForm.get('term').value;
    const selectedFilter = this.unitSearchForm.get('option').value;
    const match = this.unitSearchForm.get('tenantId')?.value?.match(/\(([^)]+)\)/);

    if (selectedFilter) {
      this._unitFilter = {
        [selectedFilter]: this.searchText ?? null,
        tenantId: match?.[1],
      };
    }

    this.isLoading = true;

    this.accessibleGroups = forkJoin([
      this._customerService.getCustomerMasterCount(this._unitFilter),
      this._customerService.getCustomerMaster(
        {
          take: this.state.take,
          skip: this.state.skip,
          filter: this.state.filter,
          sort: this.state.sort,
        },
        this._unitFilter
      ),
    ]).pipe(
      tap(loaderResult => {
        this._numberOfAccessibleGroups = loaderResult[0];
        this.isPagingRequired = this._pagingHelperService.isPagingRequired(
          this.state,
          this.pagingConfig,
          this._numberOfAccessibleGroups
        );
        this.isLoading = false;
      }),
      map(
        loaderResult =>
          <GridDataResult>{
            data: loaderResult[1],
            total: loaderResult[0],
          }
      ),
      share()
    );
  }

  public selectGroup(selectedGroup: GroupHeader): void {
    if (this.options.autoImpersonate) {
      this._impersonationService.setCurrentGroupValue(selectedGroup);
    }

    const result: ModalResult = {
      state: ResultState.Custom,
      data: selectedGroup.id,
    };

    this._notificationService.notifyInfo('Customer updated successfully!');
    this._router.navigate(['/portfolio']);
    this.close(result);
  }

  public getFilterHint() {
    return Array.from(this._filterLookup.keys())
      .map(key => this._translatePipe.transform(key))
      .join(', ');
  }

  private setFilterLookup(): void {
    this._filterLookup = new Map();
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.GROUP_NAME', 'customerName');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.CONTRACT_NUMBER', 'contractNumber');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.UNIT_ID', 'unitId');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.GROUP_VAT', 'vat');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.SITE_NAME', 'buildingName');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.SITE_ADDRESS', 'buildingAddress');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.SITE_CITY', 'buildingCity');
    this._filterLookup.set('ADMINISTRATION_OVERVIEW.UNIT_SEARCH.SITE_STATE', 'buildingState');

    const newFilter = [];
    this._filterLookup.forEach((value, key) => {
      newFilter.push({
        value: value,
        key: this._translatePipe.transform(key),
      });
    });

    this.filter = newFilter;
  }

  public closeIcon(): void {
    const result: ModalResult = { state: ResultState.Closed };
    this.close(result);
  }

  public getCurrentTenantId(): string {
    let currentCustomer = this._impersonationService.getCurrentGroup();
    if (!currentCustomer) {
      currentCustomer = this.customers[0];
    }
    const country = this.tenants?.find(({ id }) => id === currentCustomer.tenantId);
    return country?.displayName;
  }

  public goToPreviousPage(currentPage: number): void {
    if (currentPage > 1) {
      this.state.skip = (currentPage - 2) * this.state.take;
      this.loadAccessibleGroups();
    }
  }

  public goToNextPage(currentPage: number, totalPages: number): void {
    if (currentPage < totalPages) {
      this.state.skip = currentPage * this.state.take;
      this.loadAccessibleGroups();
    }
  }

  public getPageNumbers(total: number, current: number): number[] {
    if (total <= this.MaxButtonCount) return Array.from({ length: total }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(current - 2, total - this.MaxButtonCount + 1));
    return Array.from({ length: this.MaxButtonCount }, (_, i) => start + i);
  }

  public goToPage(page: number): void {
    this.grid?.pageChange.emit({
      skip: (page - 1) * this.grid.pageSize,
      take: this.grid.pageSize,
    });
  }
}
